import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
	Alert,
	Button,
	Center,
	Group,
	Loader,
	Modal,
	PinInput,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { AlertTriangle, Calendar, User } from "lucide-react";
import { useState } from "react";
import type {
	LoginFormData,
	PatientLoginData,
	PortalLoginModalProps,
} from "~/types/portal-patient.types";
import { usePortalState } from "~/utils/states";
import { dateParser, handleDateValidation } from "~/utils/utils.client";

export default function PortalLoginModal({
	opened,
	onClose,
	tenantId,
	onSelectBranchForSync,
}: PortalLoginModalProps) {
	const { _ } = useLingui();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [step, setStep] = useState<"login" | "sync" | "verify-pin">("login");
	const [syncData, setSyncData] = useState<{
		nik: string;
		dateOfBirth: string;
	} | null>(null);
	const [pinVerifyData, setPinVerifyData] = useState<{
		patientData: PatientLoginData;
		authToken: string;
	} | null>(null);
	const [pin, setPin] = useState("");
	const [pinError, setPinError] = useState(false);

	// Portal state
	const {
		setAuthenticated,
		setPortalPatient,
		setAuthToken,
		fetchBranchPatient,
	} = usePortalState();

	// Form validation
	const form = useForm<LoginFormData>({
		initialValues: {
			nik: "",
			dateOfBirth: null,
		},
		validate: {
			nik: (value) => {
				if (!value) return _(t`NIK tidak boleh kosong`);
				if (value.length !== 16) return _(t`NIK harus 16 digit`);
				if (!/^\d{16}$/.test(value)) return _(t`NIK hanya boleh berisi angka`);
				return null;
			},
			dateOfBirth: (value) => {
				if (!value) return _(t`Tanggal lahir tidak boleh kosong`);
				const today = new Date();
				const minAge = new Date();
				minAge.setFullYear(today.getFullYear() - 100);
				const maxAge = new Date();
				maxAge.setFullYear(today.getFullYear() - 1);

				if (value > maxAge)
					return _(t`Tanggal lahir tidak boleh lebih dari hari ini`);
				if (value < minAge)
					return _(t`Tanggal lahir tidak valid (maksimal 100 tahun)`);
				return null;
			},
		},
	});

	const handleLogin = async (values: LoginFormData) => {
		try {
			setLoading(true);
			setError(null);

			// Format date for API (YYYY-MM-DD format) using local timezone
			const formattedDate = values.dateOfBirth
				? `${values.dateOfBirth.getFullYear()}-${String(values.dateOfBirth.getMonth() + 1).padStart(2, "0")}-${String(values.dateOfBirth.getDate()).padStart(2, "0")}`
				: undefined;

			// Use fetch to Remix route which proxies to backend
			const response = await fetch("/api/portal/auth/login", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"x-tenant-id": tenantId,
				},
				body: JSON.stringify({
					nik: values.nik,
					date_of_birth: formattedDate,
				}),
			});

			if (!response.ok) {
				throw new Error("Failed to login");
			}

			const loginResult = await response.json();

			// Check the results structure from backend
			const loginData = loginResult.results || loginResult;
			if (loginData.success && loginData.data) {
				// Successful login
				const patientData = loginData.data;

				// Check if user has PIN set
				if (patientData.has_pin) {
					// User has PIN, require verification
					setPinVerifyData({
						patientData,
						authToken: patientData.auth_token,
					});
					setStep("verify-pin");
				} else {
					// User doesn't have PIN, complete login
					setPortalPatient({
						global_patient_id: patientData.global_patient_id,
						nik: patientData.nik,
						full_name: patientData.full_name,
						date_of_birth: patientData.date_of_birth,
						phone_number: patientData.phone_number,
						// Add data from backend if available, otherwise use defaults
						norm: patientData.norm || undefined,
						sex: patientData.sex || "M", // Backend format: M/F
						email: patientData.email || undefined,
						photo_url: patientData.photo_url || undefined,
						address: patientData.address || undefined,
						has_pin: false, // User doesn't have PIN
					});

					setAuthToken(patientData.auth_token, patientData.expires_in || 3600);
					setAuthenticated(true);

					// Fetch branch patient data after successful login
					await fetchBranchPatient();

					onClose();
				}
			} else {
				// Patient not found in global database, need sync
				setSyncData({
					nik: values.nik,
					dateOfBirth: formattedDate || "",
				});
				setStep("sync");
			}
		} catch (err) {
			console.error("Login error:", err);
			if (
				err instanceof Error &&
				err.message.includes("NIK dan tanggal lahir tidak cocok")
			) {
				setError(_(t`NIK dan tanggal lahir tidak cocok`));
			} else {
				setError(_(t`Terjadi kesalahan. Silakan coba lagi.`));
			}
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyPin = async (pinValue: string) => {
		if (!pinVerifyData) return;

		try {
			setLoading(true);
			setPinError(false);

			const response = await fetch("/api/portal-patient/profile/verify-pin", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"portal-access-token": pinVerifyData.authToken,
				},
				body: JSON.stringify({
					patient_id: pinVerifyData.patientData.global_patient_id,
					pin: pinValue,
				}),
			});

			if (!response.ok) {
				throw new Error("Invalid PIN");
			}

			const result = await response.json();

			if (result.results?.valid) {
				// PIN verified successfully, complete login
				const patientData = pinVerifyData.patientData;

				setPortalPatient({
					global_patient_id: patientData.global_patient_id,
					nik: patientData.nik,
					full_name: patientData.full_name,
					date_of_birth: patientData.date_of_birth,
					phone_number: patientData.phone_number,
					norm: patientData.norm || undefined,
					sex: patientData.sex || "M",
					email: patientData.email || undefined,
					photo_url: patientData.photo_url || undefined,
					address: patientData.address || undefined,
					has_pin: true, // User has verified PIN
				});

				setAuthToken(pinVerifyData.authToken, patientData.expires_in || 3600);
				setAuthenticated(true);

				// Fetch branch patient data after successful login
				await fetchBranchPatient();

				handleClose();

				notifications.show({
					title: _(t`Berhasil`),
					message: _(t`Login berhasil`),
					color: "green",
				});
			} else {
				throw new Error("Invalid PIN");
			}
		} catch (err) {
			console.error("PIN verification error:", err);
			setPinError(true);
			setPin("");
			notifications.show({
				title: _(t`Error`),
				message: _(t`PIN salah, silakan coba lagi`),
				color: "red",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		form.reset();
		setError(null);
		setStep("login");
		setSyncData(null);
		setPinVerifyData(null);
		setPin("");
		setPinError(false);
		onClose();
	};

	const handleSelectBranch = () => {
		if (syncData && onSelectBranchForSync) {
			onSelectBranchForSync({
				nik: syncData.nik,
				dateOfBirth: syncData.dateOfBirth,
			});
		} else {
			handleClose();
		}
	};

	if (step === "sync") {
		return (
			<Modal
				opened={opened}
				onClose={handleClose}
				title={_(t`Sinkronisasi Data Pasien`)}
				size="md"
				centered
			>
				<Stack gap="md">
					<Alert icon={<AlertTriangle size={16} />} color="orange">
						<Text size="sm">
							<Trans>
								Akun Anda belum tersinkronisasi. Silakan pilih cabang terakhir
								Anda berkunjung untuk melanjutkan.
							</Trans>
						</Text>
					</Alert>

					<Text size="sm" c="gray.7">
						<Trans>
							Kami akan memvalidasi data Anda di cabang yang dipilih dan
							melakukan sinkronisasi secara otomatis.
						</Trans>
					</Text>

					<Group justify="flex-end" gap="sm">
						<Button variant="outline" onClick={handleClose}>
							<Trans>Batal</Trans>
						</Button>
						<Button onClick={handleSelectBranch}>
							<Trans>Pilih Cabang</Trans>
						</Button>
					</Group>
				</Stack>
			</Modal>
		);
	}

	if (step === "verify-pin") {
		return (
			<Modal
				opened={opened}
				onClose={handleClose}
				title={_(t`Verifikasi PIN`)}
				size="md"
				centered
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				<Stack gap="xl" p="md">
					<div>
						<Text ta="center" size="lg" fw={600} mb="xs">
							<Trans>Masukkan PIN Anda</Trans>
						</Text>
						<Text ta="center" size="sm" c="dimmed">
							<Trans>Untuk keamanan akun, silakan masukkan PIN 6 digit</Trans>
						</Text>
					</div>

					<Center>
						<PinInput
							length={6}
							type="number"
							value={pin}
							onChange={setPin}
							size="xl"
							mask
							autoFocus
							error={pinError}
							onComplete={(value) => {
								if (value.length === 6) {
									handleVerifyPin(value);
								}
							}}
						/>
					</Center>

					{loading && (
						<Center>
							<Loader size="sm" />
						</Center>
					)}

					<Group justify="flex-end" gap="sm">
						<Button variant="outline" onClick={handleClose} disabled={loading}>
							<Trans>Batal</Trans>
						</Button>
					</Group>
				</Stack>
			</Modal>
		);
	}

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={_(t`Masuk ke Portal`)}
			size="md"
			centered
		>
			<form onSubmit={form.onSubmit(handleLogin)}>
				<Stack gap="md">
					<div style={{ textAlign: "center", marginBottom: "1rem" }}>
						<Title order={3} c="gray.9" mb="xs">
							<Trans>Selamat Datang</Trans>
						</Title>
						<Text size="sm" c="gray.6">
							<Trans>Masukkan NIK dan tanggal lahir untuk melanjutkan</Trans>
						</Text>
					</div>

					<TextInput
						label={_(t`NIK`)}
						placeholder={_(t`Masukkan 16 digit NIK`)}
						leftSection={<User size={16} />}
						maxLength={16}
						{...form.getInputProps("nik")}
						onInput={(e) => {
							// Only allow numbers
							const value = e.currentTarget.value.replace(/\D/g, "");
							form.setFieldValue("nik", value);
						}}
					/>

					<DateInput
						label={_(t`Tanggal Lahir`)}
						placeholder={_(t`Format: DD/MM/YYYY`)}
						leftSection={<Calendar size={16} />}
						maxDate={new Date()}
						valueFormat="DD/MM/YYYY"
						dateParser={(value) => dateParser(value)}
						onBlur={(event) => handleDateValidation(event.target.value)}
						{...form.getInputProps("dateOfBirth")}
					/>

					{error && (
						<Alert icon={<AlertTriangle size={16} />} color="red">
							{error}
						</Alert>
					)}

					<Group justify="flex-end" gap="sm" mt="md">
						<Button variant="outline" onClick={handleClose} disabled={loading}>
							<Trans>Batal</Trans>
						</Button>
						<Button type="submit" loading={loading}>
							{loading ? <Loader size={16} /> : <Trans>Masuk</Trans>}
						</Button>
					</Group>

					<Text size="xs" c="gray.5" ta="center" mt="md">
						<Trans>
							Dengan masuk, Anda menyetujui penggunaan data untuk keperluan
							layanan medis.
						</Trans>
					</Text>
				</Stack>
			</form>
		</Modal>
	);
}
